import React from 'react';
import { Table, Progress } from 'antd';
import { Trash2, Mail, Users } from 'lucide-react';
import { TagsDisplay } from '../../tags/elements';
import { User } from '../../../data';
import { SectionCard, StatusTag, IconAction, IconActions, EmptyState } from '../../common';
import { semantic } from '../../../theme/tokens';
import { usePromoterTags, complianceTone } from './promoterTable.controller';
import { t } from '../../../company/labels';

const initials = (name) =>
    String(name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase() || '?';

export default function PromoterTable({ promoters, loading, removePromoter, navigatePromoter }) {
    const isClientUser = User.getInstance()?.userType === 'client';
    const { availableTags, promoterTagsMap } = usePromoterTags(promoters);

    const PromoterColumns = [
        {
            title: t('promotor'),
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-100 text-xs font-semibold text-surface-600">
                        {initials(text)}
                    </span>
                    <div className="min-w-0">
                        <button
                            type="button"
                            onClick={() => navigatePromoter(record._id)}
                            className="block text-left text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline">
                            {text}
                        </button>
                        <span className="block text-xs text-surface-500">{record.rif || 'Sin RIF'}</span>
                    </div>
                </div>
            ),
            sorter: (a, b) => (a.name || '').localeCompare(b.name || '')
        },
        {
            title: 'Correo',
            dataIndex: 'email',
            key: 'email',
            render: (text) => (
                <a className="inline-flex items-center gap-1.5 text-sm text-surface-700 hover:text-primary-600" href={`mailto:${text}`}>
                    <Mail className="text-surface-400 text-[13px]" />
                    {text}
                </a>
            )
        },
        {
            title: 'Cumplimiento',
            dataIndex: 'percentage',
            key: 'percentage',
            align: 'right',
            render: (text) => {
                const value = parseFloat(text) || 0;
                const t = complianceTone(text);
                return (
                    <div className="flex items-center justify-end gap-3">
                        <Progress percent={value} size="small" showInfo={false} strokeColor={semantic[t].solid} className="!m-0 w-20" />
                        <StatusTag tone={t} className="min-w-[56px] justify-center tabular-nums">
                            {value.toFixed(1)}%
                        </StatusTag>
                    </div>
                );
            },
            sorter: (a, b) => (parseFloat(a.percentage) || 0) - (parseFloat(b.percentage) || 0)
        },
        {
            title: 'Canal',
            dataIndex: 'channel',
            key: 'channel',
            render: (text) => <StatusTag tone={text === 'moderno' ? 'info' : 'warning'}>{text === 'moderno' ? 'Moderno' : 'Tradicional'}</StatusTag>,
            filters: [
                { text: 'Moderno', value: 'moderno' },
                { text: 'Tradicional', value: 'tradicional' }
            ],
            onFilter: (value, record) => record.channel === value
        },
        {
            title: 'Etiquetas',
            dataIndex: '_id',
            key: 'tags',
            width: 200,
            render: (id) => <TagsDisplay modelType="User" modelId={id} editable={!isClientUser} />,
            filters: availableTags.map((tag) => ({
                text: tag.name,
                value: tag._id
            })),
            filterMultiple: true,
            onFilter: (value, record) => {
                const promoterTags = promoterTagsMap[record._id] || [];
                return promoterTags.some((tag) => tag._id === value);
            }
        },
        ...(!isClientUser
            ? [
                  {
                      title: 'Acciones',
                      dataIndex: '_id',
                      key: '_id',
                      align: 'right',
                      width: 96,
                      render: (text) => (
                          <IconActions>
                              <IconAction
                                  icon={<Trash2 />}
                                  label="Eliminar"
                                  danger
                                  onClick={() => removePromoter(text)}
                                  confirm={{
                                      title: `Borrar ${t('promotor')}`,
                                      description: `¿Estás seguro que deseas eliminar este ${t('promotorLower')}?`,
                                      okText: 'Sí',
                                      cancelText: 'No'
                                  }}
                              />
                          </IconActions>
                      )
                  }
              ]
            : [])
    ];

    return (
        <SectionCard title={`Equipo de ${t('promotoresLower')}`} description={`${promoters?.length || 0} ${t('promotoresLower')}`} icon={<Users />} flush bodyClassName="[&_.ant-pagination]:px-5">
            <Table
                columns={PromoterColumns}
                dataSource={promoters}
                size="middle"
                loading={loading}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <EmptyState icon={<Users />} title={`No hay ${t('promotoresLower')}`} description="Ajusta la búsqueda o el filtro de cliente" /> }}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} ${t('promotoresLower')}`
                }}
                rowKey="_id"
            />
        </SectionCard>
    );
}
